/**
 * Mega Menu Navigation
 * 
 * Navigation with dropdown mega menus containing multiple columns.
 * Ideal for sites with many pages and organized content categories.
 */

import { useState, useMemo } from "react";
import { Link, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  NavigationMenu,
  NavigationMenuContent,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuList,
  NavigationMenuTrigger,
  navigationMenuTriggerStyle,
} from "@/components/ui/navigation-menu";
import { Menu, X, Calendar, Users, Heart, Info, Briefcase, BookOpen, FileText, Newspaper, Camera, Folder, Handshake, Mail } from "lucide-react";
import { useNavigationItems } from "@/hooks/useNavigationItems";
import { useSiteConfig } from "@/lib/useSiteConfig";
import { useSiteSettings } from "@/hooks/useSiteSettings";
import { useLayout } from "@/lib/layout-provider";
import { categorizeNavigation, shouldUseCategorizedNavigation } from "@/lib/navigation-utils";
import { cn } from "@/lib/utils";

// Icon map for categories
const categoryIcons: Record<string, React.ComponentType<{ className?: string }>> = {
  Info,
  Briefcase,
  BookOpen,
  FileText,
  Newspaper,
  Calendar,
  Camera,
  Folder,
  Handshake,
  Mail,
};

const NavigationMegaMenu = () => {
  const [isOpen, setIsOpen] = useState(false);
  const location = useLocation();
  const { currentLayout } = useLayout();
  const { features, navigation } = useSiteSettings();
  const config = useSiteConfig();
  const { navigationItems: navItems } = useNavigationItems();

  const isActive = (path: string) => location.pathname === path;
  const navConfig = currentLayout.navigation;
  
  // Categorize navigation if we have many items
  const categorizedNav = useMemo(() => {
    if (shouldUseCategorizedNavigation(navItems.length)) {
      return categorizeNavigation(navItems);
    }
    return null;
  }, [navItems]);

  return (
    <nav 
      className={cn(
        "bg-background/95 backdrop-blur-sm border-b border-border z-50 shadow-warm",
        navConfig.sticky && "sticky top-0",
        navConfig.transparent && "bg-transparent border-transparent shadow-none"
      )}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center space-x-3">
            {config.logoUrl && config.logoUrl.trim() !== '' && (
              <img
                src={config.logoUrl}
                alt={config.organizationName}
                className="h-10 w-auto object-contain"
                onError={(e) => {
                  console.error('[NavigationMegaMenu] Failed to load logo:', config.logoUrl);
                  e.currentTarget.style.display = 'none';
                }}
              />
            )}
            <span className="text-xl font-bold text-foreground">
              {config.organizationName}
            </span>
          </Link>

          {/* Desktop Navigation - Intelligent Categorization */}
          <div className="hidden lg:flex items-center">
            <NavigationMenu>
              <NavigationMenuList>
                {categorizedNav ? (
                  <>
                    {/* Primary items (always visible) */}
                    {categorizedNav.primary.map((item) => (
                      <NavigationMenuItem key={item.name}>
                        <NavigationMenuLink asChild>
                          <Link 
                            to={item.path}
                            className={cn(
                              navigationMenuTriggerStyle(),
                              isActive(item.path) && "text-heritage"
                            )}
                          >
                            {item.name}
                          </Link>
                        </NavigationMenuLink>
                      </NavigationMenuItem>
                    ))}
                    
                    {/* Categorized dropdowns */}
                    {categorizedNav.categories.map((category) => {
                      const CategoryIcon = category.icon ? categoryIcons[category.icon] : null;
                      return (
                        <NavigationMenuItem key={category.name}>
                          <NavigationMenuTrigger>
                            {CategoryIcon && <CategoryIcon className="w-4 h-4 mr-2" />}
                            {category.name}
                          </NavigationMenuTrigger>
                          <NavigationMenuContent>
                            <div className="w-[400px] p-4">
                              <div className="grid gap-3">
                                {category.items.map((item) => (
                                  <NavigationMenuLink key={item.name} asChild>
                                    <Link
                                      to={item.path}
                                      className={cn(
                                        "block select-none space-y-1 rounded-md p-3 leading-none no-underline outline-none transition-colors hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground",
                                        isActive(item.path) && "bg-accent text-accent-foreground"
                                      )}
                                    >
                                      <div className="text-sm font-medium leading-none">
                                        {item.name}
                                      </div>
                                    </Link>
                                  </NavigationMenuLink>
                                ))}
                              </div>
                            </div>
                          </NavigationMenuContent>
                        </NavigationMenuItem>
                      );
                    })}
                    
                    {/* Uncategorized items as simple links */}
                    {categorizedNav.uncategorized.map((item) => (
                      <NavigationMenuItem key={item.name}>
                        <NavigationMenuLink asChild>
                          <Link 
                            to={item.path}
                            className={cn(
                              navigationMenuTriggerStyle(),
                              isActive(item.path) && "text-heritage"
                            )}
                          >
                            {item.name}
                          </Link>
                        </NavigationMenuLink>
                      </NavigationMenuItem>
                    ))}
                  </>
                ) : (
                  /* Simple list for smaller navigation sets */
                  navItems.map((item) => (
                    <NavigationMenuItem key={item.name}>
                      <NavigationMenuLink asChild>
                        <Link 
                          to={item.path}
                          className={cn(
                            navigationMenuTriggerStyle(),
                            isActive(item.path) && "text-heritage"
                          )}
                        >
                          {item.name}
                        </Link>
                      </NavigationMenuLink>
                    </NavigationMenuItem>
                  ))
                )}
              </NavigationMenuList>
            </NavigationMenu>

            {/* CTA Buttons */}
            <div className="flex items-center space-x-2 ml-6">
              {features.events.enabled && navigation.showEventsButton && (
                <Button variant="culture" size="sm" asChild>
                  <Link to="/events">
                    <Calendar className="w-4 h-4 mr-1" />
                    {features.events.buttonText}
                  </Link>
                </Button>
              )}
              {features.membership.enabled && (
                <Button variant="heritage" size="sm" asChild>
                  <Link to="/membership">
                    <Users className="w-4 h-4 mr-1" />
                    {features.membership.buttonText}
                  </Link>
                </Button>
              )}
              {features.donation.enabled && (
                <Button variant="donate" size="sm" asChild>
                  <Link to="/donate">
                    <Heart className="w-4 h-4 mr-1" />
                    {features.donation.buttonText}
                  </Link>
                </Button>
              )}
            </div>
          </div>

          {/* Mobile menu button */}
          <div className="lg:hidden">
            <button
              onClick={() => setIsOpen(!isOpen)}
              className="text-foreground hover:text-heritage"
              aria-label="Toggle menu"
              tabIndex={0}
            >
              {isOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Navigation */}
        {isOpen && (
          <div className="lg:hidden">
            <div className="px-2 pt-2 pb-3 space-y-1 bg-background border-t border-border">
              {categorizedNav ? (
                <>
                  {/* Primary items */}
                  {categorizedNav.primary.map((item) => (
                    <Link
                      key={item.name}
                      to={item.path}
                      onClick={() => setIsOpen(false)}
                      className={cn(
                        "block px-3 py-2 text-base font-medium transition-colors",
                        isActive(item.path)
                          ? "text-heritage bg-secondary"
                          : "text-foreground hover:text-heritage hover:bg-secondary"
                      )}
                    >
                      {item.name}
                    </Link>
                  ))}
                  
                  {/* Categorized items with headers */}
                  {categorizedNav.categories.map((category) => (
                    <div key={category.name} className="mt-4">
                      <div className="px-3 py-2 text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                        {category.name}
                      </div>
                      {category.items.map((item) => (
                        <Link
                          key={item.name}
                          to={item.path}
                          onClick={() => setIsOpen(false)}
                          className={cn(
                            "block px-6 py-2 text-sm font-medium transition-colors",
                            isActive(item.path)
                              ? "text-heritage bg-secondary"
                              : "text-foreground hover:text-heritage hover:bg-secondary"
                          )}
                        >
                          {item.name}
                        </Link>
                      ))}
                    </div>
                  ))}
                  
                  {/* Uncategorized items */}
                  {categorizedNav.uncategorized.length > 0 && (
                    <div className="mt-4">
                      <div className="px-3 py-2 text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                        More
                      </div>
                      {categorizedNav.uncategorized.map((item) => (
                        <Link
                          key={item.name}
                          to={item.path}
                          onClick={() => setIsOpen(false)}
                          className={cn(
                            "block px-6 py-2 text-sm font-medium transition-colors",
                            isActive(item.path)
                              ? "text-heritage bg-secondary"
                              : "text-foreground hover:text-heritage hover:bg-secondary"
                          )}
                        >
                          {item.name}
                        </Link>
                      ))}
                    </div>
                  )}
                </>
              ) : (
                navItems.map((item) => (
                  <Link
                    key={item.name}
                    to={item.path}
                    onClick={() => setIsOpen(false)}
                    className={cn(
                      "block px-3 py-2 text-base font-medium transition-colors",
                      isActive(item.path)
                        ? "text-heritage bg-secondary"
                        : "text-foreground hover:text-heritage hover:bg-secondary"
                    )}
                  >
                    {item.name}
                  </Link>
                ))
              )}
              
              {/* Mobile CTA Buttons */}
              <div className="px-3 py-4 space-y-2">
                {features.events.enabled && navigation.showEventsButton && (
                  <Button variant="culture" className="w-full" asChild>
                    <Link to="/events" onClick={() => setIsOpen(false)}>
                      <Calendar className="w-4 h-4 mr-2" />
                      {features.events.buttonText}
                    </Link>
                  </Button>
                )}
                {features.membership.enabled && (
                  <Button variant="heritage" className="w-full" asChild>
                    <Link to="/membership" onClick={() => setIsOpen(false)}>
                      <Users className="w-4 h-4 mr-2" />
                      {features.membership.buttonText}
                    </Link>
                  </Button>
                )}
                {features.donation.enabled && (
                  <Button variant="donate" className="w-full" asChild>
                    <Link to="/donate" onClick={() => setIsOpen(false)}>
                      <Heart className="w-4 h-4 mr-2" />
                      {features.donation.buttonText}
                    </Link>
                  </Button>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

export default NavigationMegaMenu;
